/**
 * Capability #12 — Unified environment intelligence (compose-on-read).
 *
 * Relates existing deployment / execution / diagnostic environment signals.
 * Does not invent health, safe-skip, or release authority.
 */
import { z } from 'zod';

/** Evidence band for a dimension or overall context — not health. */
export const ENVIRONMENT_EVIDENCE_BANDS = ['known', 'partial', 'unknown', 'conflicting'] as const;
export type EnvironmentEvidenceBand = (typeof ENVIRONMENT_EVIDENCE_BANDS)[number];
export const EnvironmentEvidenceBandSchema = z.enum(ENVIRONMENT_EVIDENCE_BANDS);

export const ENVIRONMENT_DIMENSION_KINDS = [
  'deployment_target',
  'execution_target',
  'flow_version',
  'scenario_tier',
  'failure_context',
  'diagnostic_class',
] as const;
export type EnvironmentDimensionKind = (typeof ENVIRONMENT_DIMENSION_KINDS)[number];

export const ENV_LOOKUP_SCOPES = ['project', 'org', 'unknown'] as const;
export type EnvLookupScope = (typeof ENV_LOOKUP_SCOPES)[number];

/** Write-time snapshot persisted on test_runs / test_schedules (Migration 126). */
export const EnvironmentLookupSnapshotSchema = z.object({
  envId: z.string().min(1).max(120),
  envName: z.string().min(1).max(200),
  envScope: z.enum(ENV_LOOKUP_SCOPES),
  snapshotAt: z.string(),
});
export type EnvironmentLookupSnapshot = z.infer<typeof EnvironmentLookupSnapshotSchema>;

export const EnvironmentDimensionEvidenceSchema = z.object({
  kind: z.enum(ENVIRONMENT_DIMENSION_KINDS),
  band: EnvironmentEvidenceBandSchema,
  /** Normalized display value when known or partial. */
  displayValue: z.string().max(300).nullable(),
  rawValue: z.string().max(300).nullable(),
  source: z.string().max(80),
  note: z.string().max(400).nullable(),
});
export type EnvironmentDimensionEvidence = z.infer<typeof EnvironmentDimensionEvidenceSchema>;

export const EnvironmentIntelligenceGovernanceSchema = z.object({
  autoApply: z.literal(false),
  silentPublish: z.literal(false),
  recommendationsAreNotTruth: z.literal(true),
  advisoryOnly: z.literal(true),
  claimsEnvironmentHealthy: z.literal(false),
  claimsSafeSkip: z.literal(false),
  autoBlocksRelease: z.literal(false),
});
export type EnvironmentIntelligenceGovernance = z.infer<typeof EnvironmentIntelligenceGovernanceSchema>;

export const EnvironmentSurfaceEvidenceSchema = z.object({
  band: EnvironmentEvidenceBandSchema,
  summary: z.string().max(500),
  why: z.array(z.string().max(300)).max(6).default([]),
  sampleCount: z.number().int().nonnegative().default(0),
});
export type EnvironmentSurfaceEvidence = z.infer<typeof EnvironmentSurfaceEvidenceSchema>;

export const UnifiedRunEnvironmentViewSchema = z.object({
  runId: z.string().uuid().optional(),
  failureId: z.string().uuid().optional(),
  overallBand: EnvironmentEvidenceBandSchema,
  dimensions: z.array(EnvironmentDimensionEvidenceSchema).max(12),
  hasTrace: z.boolean().default(false),
  traceNote: z.string().max(300).nullable().optional(),
});
export type UnifiedRunEnvironmentView = z.infer<typeof UnifiedRunEnvironmentViewSchema>;

export const ProjectEnvironmentIntelligenceResponseSchema = z.object({
  projectId: z.string().uuid(),
  orgId: z.string().uuid(),
  composedAt: z.string(),
  attribution: z.literal('environment_intelligence_compose'),
  governance: EnvironmentIntelligenceGovernanceSchema,
  overallBand: EnvironmentEvidenceBandSchema,
  summary: z.string().max(600),
  why: z.array(z.string().max(300)).max(8).default([]),
  dimensions: z.array(EnvironmentDimensionEvidenceSchema).max(12).default([]),
  surfaces: z.object({
    execution: EnvironmentSurfaceEvidenceSchema,
    failureIntelligence: EnvironmentSurfaceEvidenceSchema,
    trace: EnvironmentSurfaceEvidenceSchema,
    changeImpact: EnvironmentSurfaceEvidenceSchema,
    maintenance: EnvironmentSurfaceEvidenceSchema,
    releaseConfidence: EnvironmentSurfaceEvidenceSchema,
  }),
  focal: UnifiedRunEnvironmentViewSchema.nullable().optional(),
  recentSamples: z.array(UnifiedRunEnvironmentViewSchema).max(10).default([]),
});
export type ProjectEnvironmentIntelligenceResponse = z.infer<typeof ProjectEnvironmentIntelligenceResponseSchema>;

export const EnvironmentIntelligenceQuerySchema = z.object({
  runId: z.string().uuid().optional(),
  failureId: z.string().uuid().optional(),
  sha: z.string().min(7).max(64).optional(),
});
export type EnvironmentIntelligenceQuery = z.infer<typeof EnvironmentIntelligenceQuerySchema>;

/** Optional enrichments on evidence bundle / QA context (compose-only). */
export const EnvironmentIntelligenceSectionSchema = z.object({
  status: z.enum(['available', 'limited', 'unavailable']),
  overallBand: EnvironmentEvidenceBandSchema,
  summary: z.string().max(500),
  dimensions: z.array(EnvironmentDimensionEvidenceSchema).max(12).default([]),
  governance: EnvironmentIntelligenceGovernanceSchema,
});
export type EnvironmentIntelligenceSection = z.infer<typeof EnvironmentIntelligenceSectionSchema>;
